// Lib
import React from 'react';
import { split, first } from 'lodash/fp';

// Utils
import { getChildren } from '../../../../common/elements/utils/elementTraversalUtils';
import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';
import { clearOnboardingProgressStep, setOnboardingProgressStep } from './onboardingProgressService';
import { selectMostRecentBoard } from './onboardingStepsUtils';
import platformSingleton from '../../../platform/platformSingleton';

// Selectors
import { getElements } from '../../../element/selectors/elementsSelector';
import { getCurrentUser, getCurrentUserRootBoardId } from '../../../user/currentUserSelector';
import { getUserEducation } from '../../../../common/users/utils/userPropertyUtils';
import { hasSeen } from '../../../../common/users/userEducationUtil';
import { getLocationPathname } from '../../../app/routingSelector';
import { isElementId } from '../../../../common/uid/idValidator';
import { areAliasesInitialised, isCurrentUserInitialised } from '../../../app/initialisation/initialisationSelector';
import {
    getCmsTemplatesFetched,
    getOnboardingBoardTitle,
    getOnboardingTemplateBoardId,
} from '../../boardTemplatePickerPopup/boardTemplatePickerSelectors';
import {
    getCurrentBoard,
    getCurrentBoardId,
    getVisibleBoard,
    getVisibleBoardId,
} from '../../../reducers/currentBoardId/currentBoardIdSelector';
import { selectIsSocketConnected } from '../../../utils/socket/socketConnectionSelector';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';

// Actions
import { getBoardDecoration, getColor } from '../../../element/board/boardActions';
import { createElementSync, updateElement } from '../../../element/actions/elementActions';
import { navigateToBoard, navigateToRootWorkspace } from '../../../reducers/navigationActions';
import {
    clearPersistedUserOnboarding,
    markEducationCodeAsSeen,
    onboardingAddListener,
    onboardingNavigateToSegmentPicker,
    onboardingSetMetadata,
} from '../onboardingActions';

// Components
import OnboardingTooltipCloseButton from '../tooltips/OnboardingTooltipCloseButton';
import OnboardingTooltipButton from '../tooltips/OnboardingTooltipButton';
import OnboardingTooltipContentSuggestBoardNames from '../tooltips/OnboardingTooltipContentSuggestBoardNames';

// Constants
import { ONBOARDING_EDUCATION_CODES } from '../../../../common/users/userEducationConstants';
import {
    ELEMENTS_DESELECTED,
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_SELECTED,
} from '../../../../common/elements/selectionConstants';
import { POPUP_TOGGLE } from '../../../components/popupPanel/popupConstants';
import { DRAG_START } from '../../../reducers/draggingConstants';
import { ONBOARDING_STEP_PASSED } from '../onboardingConstants';
import { BoardSections } from '../../../../common/boards/boardConstants';
import { ElementType } from '../../../../common/elements/elementTypes';
import { OnboardingFlow, OnboardingTooltipConfig } from './onboardingConfigTypes';
import { TooltipPositions } from '../../../components/tooltips/tooltipConstants';
import { OnboardingStepsAddTemplateMobile } from './onboardingStepsAddTemplateMobile';

import { ELEMENT_CREATE, ELEMENT_UPDATE } from '../../../../common/elements/elementConstants';
import { CURRENT_BOARD_ID_SET } from '../../../reducers/currentBoardId/currentBoardIdConstants';

export enum OnboardingStepsAddTemplate {
    CREATE_BOARD_1 = 'use-template-create-board-1',
    CREATE_BOARD_1A_DRAGGING_BOARD = 'use-template-create-board-1a-dragging-board',
    CREATE_BOARD_1B_NAME_BOARD = 'use-template-create-board-1b-name-board',
    CREATE_BOARD_1C_NAVIGATE_TO_BOARD = 'use-template-create-board-1c-navigate-to-board',
    CREATE_BOARD_2_WAIT_FOR_USE = 'use-template-create-board-2-wait-for-use',
    CREATE_BOARD_3_AFTER_USE = 'use-template-create-board-3-after-use',

    NEW_USER_0_PREPARE = 'use-template-new-user-0-prepare',
    NEW_USER_1_INIT_BOARD = 'use-template-new-user-1-init-board',
    NEW_USER_2_SEGMENT_PICKER = 'use-template-new-user-2-segment-picker',
    NEW_USER_3_WAIT_FOR_USE = 'use-template-new-user-3-wait-for-use',
    NEW_USER_4_EXPLAIN_TOOLBAR = 'use-template-new-user-4-explain-toolbar',
    NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN = 'use-template-new-user-4a-explain-toolbar-hidden',

    RETURN_HOME_USED_TEMPLATE = 'return-home-used-template',
    EXPLAIN_BREADCRUMBS_FIRST_SESSION = 'use-template-explain-breadcrumbs-first-session',
    EXPLAIN_BREADCRUMBS_SECOND_SESSION = 'use-template-explain-breadcrumbs-second-session',
    MAIN = 'use-template',
}

const getRootBoardHasChildren = (state: any) => {
    const rootBoardId = getCurrentUserRootBoardId(state);
    const elements = getElements(state);
    const rootBoardChildrenIds = getChildren(elements, rootBoardId);
    return rootBoardChildrenIds.size > 0;
};

const getIsBoardRoute = (routePath: string) => {
    const parts = split('/', routePath).filter((part) => part.length);

    return isElementId(first(parts));
};

const attachOnboardingListenerEnterBoard = (gotoStep: string) => (dispatch: Function) => {
    dispatch(
        onboardingAddListener({
            id: 'template-onboarding-enter-board',
            predicate: (action: any, state: any) => {
                if (action.type !== 'CURRENT_BOARD_ID_SET') return false;

                const rootBoardId = getCurrentUserRootBoardId(state);

                return action.boardId !== rootBoardId;
            },
            gotoStep,
        }),
    );
};

const EXPLAIN_BREADCRUMBS_TIP_CONFIG: OnboardingTooltipConfig = {
    selector: '.Breadcrumb.current',
    position: TooltipPositions.BOTTOM,
    distance: 10,
    component: () => (
        <div>
            <div className="primary-text">Notice that the top bar has updated to show you’re inside the board</div>
            <div className="footer">
                <OnboardingTooltipButton gotoStep="onboarding-finish">OK, got it</OnboardingTooltipButton>
            </div>
        </div>
    ),
};

/*
 * Template onboarding flow reference diagram:
 * https://app.milanote.com/1OQKiN1Q5xt52a/built-onboarding-flow?p=6wl8UaMQoBe
 *
 */

const ADD_TEMPLATE_STEPS_DRAG_OUT_BOARD: OnboardingFlow = {
    [OnboardingStepsAddTemplate.CREATE_BOARD_1]: {
        id: OnboardingStepsAddTemplate.CREATE_BOARD_1,
        callback: () => {
            clearOnboardingProgressStep();
        },
        nextStep: (action) => {
            if (action.type === DRAG_START) return OnboardingStepsAddTemplate.CREATE_BOARD_1A_DRAGGING_BOARD;
        },
        tip: {
            selector: '#element-tool-board',
            position: TooltipPositions.RIGHT,
            distance: 10,
            component: () => (
                <div>
                    <OnboardingTooltipCloseButton />

                    <div className="primary-text">Drag out a board to create your template</div>
                </div>
            ),
            style: {
                transform: 'translate(12px, 2px)',
                width: '25rem',
            },
        },
    },
    [OnboardingStepsAddTemplate.CREATE_BOARD_1A_DRAGGING_BOARD]: {
        id: OnboardingStepsAddTemplate.CREATE_BOARD_1A_DRAGGING_BOARD,
        nextStep: (action) => {
            if (action.type === ELEMENT_CREATE) return OnboardingStepsAddTemplate.CREATE_BOARD_1B_NAME_BOARD;
        },
    },
    [OnboardingStepsAddTemplate.CREATE_BOARD_1B_NAME_BOARD]: {
        id: OnboardingStepsAddTemplate.CREATE_BOARD_1B_NAME_BOARD,
        nextStep: (action) => {
            switch (action.type) {
                case ELEMENT_UPDATE:
                    return OnboardingStepsAddTemplate.CREATE_BOARD_1C_NAVIGATE_TO_BOARD;
                default:
                    return;
            }
        },
        tip: {
            selector: selectMostRecentBoard,
            position: TooltipPositions.BOTTOM,
            distance: 15,
            component: () => (
                <div>
                    <OnboardingTooltipCloseButton />
                    <div className="primary-text">Let’s give your new board a name</div>
                    <OnboardingTooltipContentSuggestBoardNames />
                </div>
            ),
            style: {
                width: '35rem',
            },
        },
    },
    [OnboardingStepsAddTemplate.CREATE_BOARD_1C_NAVIGATE_TO_BOARD]: {
        id: OnboardingStepsAddTemplate.CREATE_BOARD_1C_NAVIGATE_TO_BOARD,
        nextStep: (action) => {
            switch (action.type) {
                case CURRENT_BOARD_ID_SET:
                    return OnboardingStepsAddTemplate.CREATE_BOARD_2_WAIT_FOR_USE;
                default:
                    return;
            }
        },
        tip: {
            selector: (action, state) => `${selectMostRecentBoard(action, state)} .BoardIcon`,
            position: TooltipPositions.RIGHT,
            distance: 15,
            component: () => (
                <div>
                    <OnboardingTooltipCloseButton />
                    <div className="primary-text">
                        {platformSingleton.features.supportsMouse
                            ? 'Great, now double click the board to open it'
                            : 'Great, now double tap the board to open it'}
                    </div>
                </div>
            ),
        },
    },
    [OnboardingStepsAddTemplate.CREATE_BOARD_2_WAIT_FOR_USE]: {
        id: OnboardingStepsAddTemplate.CREATE_BOARD_2_WAIT_FOR_USE,
        nextStep: (action) => {
            if (action.type === ONBOARDING_STEP_PASSED && action.step === 'template-picker') {
                return OnboardingStepsAddTemplate.CREATE_BOARD_3_AFTER_USE;
            }
        },
    },
    [OnboardingStepsAddTemplate.CREATE_BOARD_3_AFTER_USE]: {
        id: OnboardingStepsAddTemplate.CREATE_BOARD_3_AFTER_USE,
        dispatch: () => (dispatch) => {
            dispatch(
                onboardingSetMetadata({
                    landing_page: null,
                    select_template: null,
                    board_title: null,
                }),
            );
        },
        nextStep: () => {
            return 'onboarding-finish';
        },
    },
};

const ADD_TEMPLATE_STEPS_FLOW_CONFIG_NEW_USER: OnboardingFlow = {
    [OnboardingStepsAddTemplate.NEW_USER_0_PREPARE]: {
        id: OnboardingStepsAddTemplate.NEW_USER_0_PREPARE,
        nextStep: (action, state) => {
            const userInitialisationComplete = areAliasesInitialised(state) && isCurrentUserInitialised(state);

            const socketConnected = selectIsSocketConnected(state);

            if (userInitialisationComplete && socketConnected) return OnboardingStepsAddTemplate.NEW_USER_1_INIT_BOARD;
        },
    },
    [OnboardingStepsAddTemplate.NEW_USER_1_INIT_BOARD]: {
        id: OnboardingStepsAddTemplate.NEW_USER_1_INIT_BOARD,
        dispatch: () => async (dispatch, getState) => {
            const state = getState();

            dispatch(
                onboardingSetMetadata({
                    hideTemplatePicker: true,
                }),
            );

            dispatch(markEducationCodeAsSeen(ONBOARDING_EDUCATION_CODES.DESKTOP_ONBOARDING));

            // if we get to this point and there _are_ children, on the root board (shouldn't happen),
            // bail from this step
            if (getRootBoardHasChildren(state)) return;

            const rootBoardId = getCurrentUserRootBoardId(state);
            const boardTitle = getOnboardingBoardTitle(state) || 'New Board';

            const color = getColor(boardTitle, state);

            const elementId = dispatch(
                createElementSync({
                    elementType: ElementType.BOARD_TYPE,
                    location: {
                        parentId: rootBoardId,
                        section: BoardSections.CANVAS,
                        position: {
                            x: 10,
                            y: 8,
                        },
                    },
                    content: {
                        title: boardTitle,
                        color,
                    },
                    transactionId: -1,
                    silent: true,
                }),
            );

            dispatch(
                onboardingSetMetadata({
                    templateBoardId: elementId,
                }),
            );

            dispatch(navigateToBoard({ boardId: elementId, replaceUrl: true, trackTiming: false }));
        },
        nextStep: (action, state) => {
            const templatesLoaded = getCmsTemplatesFetched(state);
            const templateBoardId = getOnboardingTemplateBoardId(state);
            const currentBoardId = getCurrentBoardId(state);
            const hasChangedVisibleBoard = getVisibleBoardId(state) !== currentBoardId && !!getVisibleBoard(state);

            // before proceeding to next step, we need to make sure that...
            if (
                templatesLoaded && // templates are loaded
                templateBoardId === currentBoardId && // we have navigated to the new board board
                hasChangedVisibleBoard // the current visible board has changed to the template, and that board is available
            )
                return OnboardingStepsAddTemplate.NEW_USER_2_SEGMENT_PICKER;
        },
        callback: () => {
            sendAmplitudeEvent({ eventType: 'Started onboarding tutorial' });
        },
    },
    [OnboardingStepsAddTemplate.NEW_USER_2_SEGMENT_PICKER]: {
        id: OnboardingStepsAddTemplate.NEW_USER_2_SEGMENT_PICKER,
        dispatch: () => async (dispatch, getState) => {
            const state = getState();
            const currentBoard = getCurrentBoard(getState());

            dispatch(onboardingNavigateToSegmentPicker());

            // This element update with board icon happens after the board is created,
            // so that the user won't have to wait for icon search results to come back
            // before we can create the board
            try {
                const { icon } = await getBoardDecoration({
                    id: undefined,
                    title: currentBoard.getIn(['content', 'title']),
                    state,
                });

                dispatch(
                    updateElement({
                        id: currentBoard.get('id'),
                        changes: {
                            icon,
                        },
                        transactionId: -1,
                        silent: true,
                    }),
                );
            } catch (error) {
                // ignore
            }
        },
        callback: () => {
            setOnboardingProgressStep(OnboardingStepsAddTemplate.NEW_USER_2_SEGMENT_PICKER);
        },
        nextStep: (action) => {
            if (action.type === 'MODAL_CLOSE') return OnboardingStepsAddTemplate.NEW_USER_3_WAIT_FOR_USE;
        },
    },
    [OnboardingStepsAddTemplate.NEW_USER_3_WAIT_FOR_USE]: {
        id: OnboardingStepsAddTemplate.NEW_USER_3_WAIT_FOR_USE,
        nextStep: (action) => {
            if (action.type === ONBOARDING_STEP_PASSED && action.step === 'template-picker') {
                return OnboardingStepsAddTemplate.NEW_USER_4_EXPLAIN_TOOLBAR;
            }
        },
        callback: () => {
            setOnboardingProgressStep(OnboardingStepsAddTemplate.RETURN_HOME_USED_TEMPLATE);
        },
        dispatch: () => (dispatch) => {
            dispatch(
                onboardingSetMetadata({
                    hideTemplatePicker: null,
                }),
            );

            dispatch(attachOnboardingListenerEnterBoard(OnboardingStepsAddTemplate.EXPLAIN_BREADCRUMBS_FIRST_SESSION));
        },
    },
    [OnboardingStepsAddTemplate.NEW_USER_4_EXPLAIN_TOOLBAR]: {
        id: OnboardingStepsAddTemplate.NEW_USER_4_EXPLAIN_TOOLBAR,
        nextStep: (action) => {
            switch (action.type) {
                case DRAG_START:
                case POPUP_TOGGLE:
                    return 'onboarding-finish';
                case ELEMENTS_SELECTED:
                    return OnboardingStepsAddTemplate.NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN;
                default:
                    return;
            }
        },
        dispatch: () => (dispatch) => {
            dispatch(
                // Ensure the auto-selected template only happens once
                onboardingSetMetadata({
                    landing_page: null,
                    select_template: null,
                }),
            );
            dispatch(markEducationCodeAsSeen(ONBOARDING_EDUCATION_CODES.DESKTOP_ONBOARDING));
        },
        callback: () => {
            setOnboardingProgressStep(OnboardingStepsAddTemplate.RETURN_HOME_USED_TEMPLATE);
            sendAmplitudeEvent({ eventType: 'Finished onboarding tutorial' });
        },
        tip: {
            selector: '.Toolbar:not(.disabled) .element-tool-card',
            position: TooltipPositions.RIGHT,
            offset: 60,
            distance: 10,
            component: () => (
                <div>
                    <div className="primary-text">Now you&apos;re ready to add some content to the board</div>
                    <div className="secondary-text">
                        Try adding some notes,
                        <br />
                        images and links
                    </div>
                    <div className="footer">
                        <OnboardingTooltipButton gotoStep="onboarding-finish" style={{ width: '10rem' }}>
                            OK
                        </OnboardingTooltipButton>
                    </div>
                </div>
            ),
            arrowStyle: {
                transform: 'translate(6rem, 0)',
            },
            style: {
                transform: 'translate(13px, 0)',
                width: '27rem',
            },
        },
    },
    [OnboardingStepsAddTemplate.NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN]: {
        id: OnboardingStepsAddTemplate.NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN,
        nextStep: (action) => {
            switch (action.type) {
                case ELEMENTS_DESELECTED:
                case ELEMENTS_DESELECT_ALL:
                    return OnboardingStepsAddTemplate.NEW_USER_4_EXPLAIN_TOOLBAR;
                default:
                    return;
            }
        },
    },
};

const ADD_TEMPLATE_STEPS_FLOW_CONFIG_NEW_USER_SECOND_SESSION: OnboardingFlow = {
    [OnboardingStepsAddTemplate.RETURN_HOME_USED_TEMPLATE]: {
        id: OnboardingStepsAddTemplate.RETURN_HOME_USED_TEMPLATE,
        dispatch: () => (dispatch, getState) => {
            // Attach onboarding listener to explain board navigation
            const state = getState();
            const education = getUserEducation(getCurrentUser(state));
            if (!hasSeen(education, ONBOARDING_EDUCATION_CODES.EXPLAIN_BOARD_BREADCRUMBS)) {
                dispatch(
                    attachOnboardingListenerEnterBoard(OnboardingStepsAddTemplate.EXPLAIN_BREADCRUMBS_SECOND_SESSION),
                );
            } else {
                // if the user has seen this listener, clear onboarding progress and allow normal usage of the app
                clearOnboardingProgressStep();
            }

            // Navigate to home board
            const routePath = getLocationPathname(getState());
            const isBoardRoute = getIsBoardRoute(routePath);
            if (!isBoardRoute) return;

            return dispatch(navigateToRootWorkspace(true));
        },
        nextStep: () => 'onboarding-finish',
    },

    // Onboarding tooltip for the breadcrumbs
    [OnboardingStepsAddTemplate.EXPLAIN_BREADCRUMBS_FIRST_SESSION]: {
        id: OnboardingStepsAddTemplate.EXPLAIN_BREADCRUMBS_FIRST_SESSION,
        tip: EXPLAIN_BREADCRUMBS_TIP_CONFIG,
        dispatch: () => (dispatch) => {
            // if we're in the first session, set the save step to return home in the second session
            setOnboardingProgressStep('return-home');
            dispatch(markEducationCodeAsSeen(ONBOARDING_EDUCATION_CODES.EXPLAIN_BOARD_BREADCRUMBS));
        },
    },

    // Onboarding tooltip for the breadcrumbs
    [OnboardingStepsAddTemplate.EXPLAIN_BREADCRUMBS_SECOND_SESSION]: {
        id: OnboardingStepsAddTemplate.EXPLAIN_BREADCRUMBS_SECOND_SESSION,
        tip: EXPLAIN_BREADCRUMBS_TIP_CONFIG,
        dispatch: () => (dispatch) => {
            clearOnboardingProgressStep();
            dispatch(markEducationCodeAsSeen(ONBOARDING_EDUCATION_CODES.EXPLAIN_BOARD_BREADCRUMBS));
        },
    },
};

const ADD_TEMPLATE_STEPS: OnboardingFlow = {
    // Config for sub-flows
    ...ADD_TEMPLATE_STEPS_DRAG_OUT_BOARD,

    ...ADD_TEMPLATE_STEPS_FLOW_CONFIG_NEW_USER,
    ...ADD_TEMPLATE_STEPS_FLOW_CONFIG_NEW_USER_SECOND_SESSION,

    // Entry point for template onboarding
    [OnboardingStepsAddTemplate.MAIN]: {
        id: OnboardingStepsAddTemplate.MAIN,
        nextStep: (_, state) => {
            const isMobileAppMode = selectIsMobileAppMode(state);
            if (isMobileAppMode) return OnboardingStepsAddTemplateMobile.MAIN;

            const currentUser = getCurrentUser(state);
            const education = getUserEducation(currentUser);

            const isExistingUser = hasSeen(education, ONBOARDING_EDUCATION_CODES.DESKTOP_ONBOARDING);
            if (isExistingUser) return OnboardingStepsAddTemplate.CREATE_BOARD_1;

            return OnboardingStepsAddTemplate.NEW_USER_0_PREPARE;
        },
        dispatch: () => (dispatch) => {
            dispatch(clearPersistedUserOnboarding());
        },
        callback: () => {
            clearOnboardingProgressStep();
        },
    },
};

export default ADD_TEMPLATE_STEPS;
